import React from 'react';
import { Link } from 'react-router-dom';

import {
  Chip, useThumbnail, tallyFrame, videoTone, commsTone, presenceTone, tempTone, ago,
  TALLY_LABEL, TALLY_TONE, SEVERITY_TONE,
} from './fleetUi';

/** One camera in the Fleet grid (PRD F8). The whole tile opens the box. */
export default function BoxTile({ box, now }) {
  const thumb = useThumbnail(box.id, box.online ? box.thumb_seq : 0);
  const v = box.video;
  const worst = box.alerts[0];

  return (
    <Link
      to={`/fleet/${box.id}`}
      className="group block rounded-lg overflow-hidden bg-app-well border border-app-line-chrome hover:border-gray-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      style={tallyFrame(box.tally, box.online)}
    >
      <div className={`relative aspect-video bg-black ${box.online ? '' : 'opacity-40 grayscale'}`}>
        {thumb && box.online
          ? <img src={thumb} alt="" className="w-full h-full object-cover" />
          : (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500">
              {box.online ? 'No picture yet' : 'Offline'}
            </div>
          )}
        {box.online && box.tally && box.tally !== 'off' && (
          <span className="absolute top-1.5 left-1.5"><Chip tone={TALLY_TONE[box.tally]}>{TALLY_LABEL[box.tally]}</Chip></span>
        )}
        {!box.in_show && (
          <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded text-[11px] bg-black/60 text-gray-300">Not in show</span>
        )}
      </div>

      <div className="px-3 py-2.5 space-y-2">
        <div className="flex items-baseline justify-between gap-2 min-w-0">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-app-fg truncate">{box.name}</div>
            {box.source_name && box.source_name !== box.name && (
              <div className="text-xs text-gray-500 truncate">{box.source_name}</div>
            )}
          </div>
          {box.version && <span className="font-mono text-[13px] text-gray-500 flex-shrink-0">v{box.version}</span>}
        </div>

        {box.online ? (
          <>
            <div className="flex flex-wrap gap-1">
              <Chip tone={videoTone(v?.state)} title="Video">{v?.state === 'LIVE' ? (v.format || 'Live') : (v?.state || 'Video ?').replace('_', ' ')}</Chip>
              <Chip tone={commsTone(box.comms?.state)} title={box.comms?.channel ? `Comms: ${box.comms.channel}` : 'Comms'}>
                Comms {box.comms?.state === 'READY' ? (box.comms.channel || 'ready') : (box.comms?.state || '?').toLowerCase().replace('_', ' ')}
              </Chip>
              <Chip tone={presenceTone(box.audio?.headset)} title="Headset">Headset</Chip>
              <Chip tone={presenceTone(box.audio?.programme)} title="Programme audio">PGM audio</Chip>
            </div>
            <div className="flex items-center gap-3 font-mono text-[13px] text-gray-400">
              <span title="Frames per second">{v ? `${Number(v.fps).toFixed(v.fps % 1 ? 2 : 0)} fps` : '–'}</span>
              <span title="Dropped frames" style={v?.dropped > 0 ? { color: 'var(--next)' } : undefined}>{v ? `${v.dropped} drop` : ''}</span>
              <span title="CPU temperature" style={tempTone(box.cpu_temp_c) !== 'ok' ? { color: `var(${tempTone(box.cpu_temp_c) === 'bad' ? '--live' : '--next'})` } : undefined}>
                {box.cpu_temp_c != null ? `${Math.round(box.cpu_temp_c)}°C` : ''}
              </span>
            </div>
          </>
        ) : (
          <div className="text-xs text-gray-500">Offline · last seen {ago(box.last_seen_at, now)}</div>
        )}

        {worst && (
          <div className="flex items-center gap-1.5 text-xs min-w-0">
            <Chip tone={SEVERITY_TONE[worst.severity]}>{worst.severity}</Chip>
            <span className="text-gray-400 truncate">{worst.message}</span>
            {box.alerts.length > 1 && <span className="text-gray-500 flex-shrink-0">+{box.alerts.length - 1}</span>}
          </div>
        )}
      </div>
    </Link>
  );
}
